import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { getIntegrationObservability } from "@/lib/integrations.functions";
import { getSystemObservability } from "@/lib/system-observability.functions";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Activity, AlertTriangle, CheckCircle2, RefreshCw, Timer, Cpu, ScrollText } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/admin/observability")({
  component: ObservabilityPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="p-6 space-y-3">
        <p className="text-destructive">Failed to load observability: {error.message}</p>
        <Button
          onClick={() => {
            router.invalidate();
            reset();
          }}
        >
          Retry
        </Button>
      </div>
    );
  },
  notFoundComponent: () => <div className="p-6">Not found</div>,
});

type Row = {
  tenant_id: string;
  connection_id: string;
  kind: string;
  direction: string;
  total: number;
  errors: number;
  successes: number;
  p50_ms: number;
  p95_ms: number;
  last_error_at: string | null;
  last_event_at: string | null;
};

function ObservabilityPage() {
  const fetchObs = useServerFn(getIntegrationObservability);
  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ["admin", "observability"],
    queryFn: () => fetchObs(),
    refetchInterval: 30_000,
  });

  const rows: Row[] = (data?.stats ?? []) as Row[];
  const connMap = useMemo(() => {
    const m = new Map<string, { provider: string; label: string; env: string; status: string }>();
    (data?.connections ?? []).forEach((c) =>
      m.set(c.id, { provider: c.provider, label: c.label, env: c.env, status: c.status }),
    );
    return m;
  }, [data]);

  const totals = useMemo(() => {
    const t = rows.reduce(
      (acc, r) => {
        acc.total += r.total;
        acc.errors += r.errors;
        acc.successes += r.successes;
        acc.p95 = Math.max(acc.p95, r.p95_ms);
        return acc;
      },
      { total: 0, errors: 0, successes: 0, p95: 0 },
    );
    return {
      ...t,
      errorRate: t.total === 0 ? 0 : (t.errors / t.total) * 100,
    };
  }, [rows]);

  const sorted = useMemo(
    () => [...rows].sort((a, b) => b.errors - a.errors || b.total - a.total),
    [rows],
  );

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Integration Observability"
        description="Last 24h of webhook, EDI and carrier API activity per connection."
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isRefetching}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isRefetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      />

      <div className="grid gap-4 md:grid-cols-4">
        <SummaryCard icon={Activity} label="Events (24h)" value={totals.total.toLocaleString()} />
        <SummaryCard
          icon={CheckCircle2}
          label="Successes"
          value={totals.successes.toLocaleString()}
          tone="success"
        />
        <SummaryCard
          icon={AlertTriangle}
          label="Error rate"
          value={`${totals.errorRate.toFixed(2)}%`}
          tone={totals.errorRate > 1 ? "danger" : "muted"}
        />
        <SummaryCard
          icon={Timer}
          label="Worst p95 latency"
          value={`${totals.p95.toLocaleString()} ms`}
          tone={totals.p95 > 3000 ? "danger" : "muted"}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>By connection × kind</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : sorted.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No integration activity in the last 24 hours.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Connection</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead>Direction</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Errors</TableHead>
                  <TableHead className="text-right">Error %</TableHead>
                  <TableHead className="text-right">p50</TableHead>
                  <TableHead className="text-right">p95</TableHead>
                  <TableHead>Last error</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((r) => {
                  const conn = connMap.get(r.connection_id);
                  const errPct = r.total === 0 ? 0 : (r.errors / r.total) * 100;
                  return (
                    <TableRow key={`${r.connection_id}:${r.kind}:${r.direction}`}>
                      <TableCell>
                        <div className="font-medium">{conn?.label ?? r.connection_id.slice(0, 8)}</div>
                        <div className="text-xs text-muted-foreground">
                          {conn?.provider} · {conn?.env}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{r.kind}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{r.direction}</Badge>
                      </TableCell>
                      <TableCell className="text-right">{r.total.toLocaleString()}</TableCell>
                      <TableCell className="text-right">
                        {r.errors > 0 ? (
                          <span className="text-destructive font-medium">{r.errors}</span>
                        ) : (
                          r.errors
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <span
                          className={
                            errPct > 5
                              ? "text-destructive font-medium"
                              : errPct > 1
                                ? "text-warning"
                                : ""
                          }
                        >
                          {errPct.toFixed(1)}%
                        </span>
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs">{r.p50_ms}ms</TableCell>
                      <TableCell className="text-right font-mono text-xs">
                        <span className={r.p95_ms > 3000 ? "text-destructive" : ""}>
                          {r.p95_ms}ms
                        </span>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {r.last_error_at ? new Date(r.last_error_at).toLocaleString() : "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <SystemSection />
    </div>
  );
}

function SystemSection() {
  const fetchSys = useServerFn(getSystemObservability);
  const { data, isLoading, isError, error, refetch, isRefetching } = useQuery({
    queryKey: ["admin", "observability", "system"],
    queryFn: () => fetchSys(),
    refetchInterval: 30_000,
    retry: false,
  });

  if (isError) {
    const msg = error instanceof Error ? error.message : String(error);
    if (/forbidden/i.test(msg)) return null;
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cpu className="h-4 w-4" /> System performance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-destructive">Failed to load: {msg}</p>
        </CardContent>
      </Card>
    );
  }

  const rpcs = data?.rpcs ?? [];
  const logs = data?.logs ?? [];

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Cpu className="h-4 w-4" /> RPC performance (last hour)
          </CardTitle>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isRefetching}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isRefetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : rpcs.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No RPC samples recorded in the last hour. Wrap handlers with{" "}
              <code className="font-mono text-xs">measureRpc()</code> to populate this view.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>RPC</TableHead>
                  <TableHead className="text-right">Calls</TableHead>
                  <TableHead className="text-right">Errors</TableHead>
                  <TableHead className="text-right">Avg</TableHead>
                  <TableHead className="text-right">p50</TableHead>
                  <TableHead className="text-right">p95</TableHead>
                  <TableHead className="text-right">p99</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rpcs.map((r) => (
                  <TableRow key={r.rpc_name}>
                    <TableCell className="font-mono text-xs">{r.rpc_name}</TableCell>
                    <TableCell className="text-right">{r.calls.toLocaleString()}</TableCell>
                    <TableCell className="text-right">
                      {r.errors > 0 ? (
                        <span className="text-destructive font-medium">{r.errors}</span>
                      ) : (
                        r.errors
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">{r.avg_ms}ms</TableCell>
                    <TableCell className="text-right font-mono text-xs">{r.p50_ms}ms</TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      <span className={r.p95_ms > 1000 ? "text-destructive" : ""}>{r.p95_ms}ms</span>
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      <span className={r.p99_ms > 2000 ? "text-destructive" : ""}>{r.p99_ms}ms</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ScrollText className="h-4 w-4" /> Recent warnings & errors (24h)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : logs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No warnings or errors in the last 24 hours.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[160px]">Time</TableHead>
                  <TableHead className="w-[80px]">Level</TableHead>
                  <TableHead className="w-[160px]">Source</TableHead>
                  <TableHead>Message</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((l: any) => (
                  <TableRow key={l.id}>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(l.occurred_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Badge variant={l.level === "error" ? "destructive" : "outline"}>
                        {l.level}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{l.source ?? "—"}</TableCell>
                    <TableCell className="text-xs">{l.message}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function SummaryCard({
  icon: Icon,
  label,
  value,
  tone = "muted",
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  tone?: "muted" | "success" | "danger";
}) {
  const toneClass =
    tone === "danger"
      ? "text-destructive"
      : tone === "success"
        ? "text-success"
        : "text-muted-foreground";
  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`p-2 rounded-md bg-muted ${toneClass}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-xs text-muted-foreground">{label}</div>
          <div className="text-2xl font-semibold">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}